import React, { useEffect, useMemo, useState, useSyncExternalStore } from "react";

import { ReactSVG } from "react-svg";

import ghostIcon from "../../assets/svg/ghost.svg";
import {
   avatarColor,
   avatarInitials,
   getAvatarChangeVersion,
   resolveUserPhotoUrl,
   subscribeAvatarChanges,
} from "../../utils/userAvatar";

const cx = (...a) => a.filter(Boolean).join(" ");

/**
 * AvatarUI — singurul avatar al aplicației: poza omului, dacă are una (încărcată în
 * platformă sau de la Google), altfel inițialele pe un cerc colorat. Același om are
 * aceeași culoare peste tot (Profil, Personal, Studenți, Grupe). Înlocuiește cercurile
 * scrise de mână (studentItem__avatar, studentsGroupsUI__avatar, instructorProfileUI__avatar,
 * staffProfileUI__avatar…) și pe UserAvatar, care acum îl folosește.
 * Stilurile: scss/UI/_avatarUI.scss.
 *
 * person: omul (caută poza în avatarUrl / avatarFile / picture / photoUrl…, inițialele
 *    în firstName / lastName) · name: în loc de person, un nume (grupa: „Grupa Botanica” → „GB”)
 * src: poza dată direct (are întâietate; de ex. previzualizarea unei poze alese)
 * initials / color: în locul celor calculate (culoarea instructorului din calendar)
 * size: sm (32px) | md (48px, implicit: rândurile de listă) | lg (64px) | xl (96px: profilul)
 *    | null = mărimea o dă className (cercurile vechi, prin UserAvatar)
 * photo: false = doar inițialele · alt: textul pozei (fără el, avatarul e decorativ)
 * guest: oaspetele — o fantomă în locul inițialelor (implicit din person.isGuest), pe același cerc
 * className / style: pe cerc
 */
export default function AvatarUI({
   person = null,
   name,
   src,
   initials,
   color,
   size = "md",
   photo = true,
   guest,
   alt = "",
   className = "",
   textClassName = "",
   style,
}) {
   const source = person || name || "";
   // o poză schimbată în fila asta (profil, Personal) se vede pe loc în toate cercurile omului
   const avatarVersion = useSyncExternalStore(subscribeAvatarChanges, getAvatarChangeVersion, getAvatarChangeVersion);
   const photoUrl = useMemo(
      () => src || (photo && person ? resolveUserPhotoUrl(person) : null),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [src, photo, person, avatarVersion],
   );
   const [failed, setFailed] = useState(false);
   useEffect(() => setFailed(false), [photoUrl]);
   const isGuest = guest ?? person?.isGuest === true;
   const showPhoto = !isGuest && Boolean(photoUrl) && !failed;

   return (
      <span
         className={cx("avatarUI", size && `avatarUI--${size}`, showPhoto && "has-photo", isGuest && "is-guest", className)}
         title={isGuest ? "Oaspete" : undefined}
         aria-hidden={alt ? undefined : true}
         style={{ "--avatarUI-bg": color || avatarColor(source), ...style }}
      >
         {isGuest ? (
            <ReactSVG src={ghostIcon} className="avatarUI__ghost" wrapper="span" />
         ) : showPhoto ? (
            <img
               className="avatarUI__photo userAvatar__photo"
               src={photoUrl}
               alt={alt}
               loading="lazy"
               decoding="async"
               referrerPolicy="no-referrer"
               onError={() => setFailed(true)}
            />
         ) : (
            <span className={cx("avatarUI__initials", textClassName)}>
               {initials || avatarInitials(source)}
            </span>
         )}
      </span>
   );
}
